import { useCallback, useMemo, useState } from "react";
import { useRevealDownload } from "@/hooks/useRevealDownload";
import { ToolContainer, FileDropZone, ProcessingState, DownloadButton } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { type Mesh, meshChecks, meshStats, scaleMesh, weldVertices, writeSTLAscii, writeSTLBinary, writeOBJ, writePLYAscii, writePLYBinary, writeGLB } from "@/lib/mesh3d";
import { detectMeshFormat, guessUnits, loadMeshFile, meshProblem, MESH_ACCEPT, UNIT_FACTORS_TO_METRES, type MeshFormat } from "./meshLoad";
import { simplifyMesh } from "@/lib/meshSimplify";
import { write3MF } from "@/lib/threemf";
import MeshReport from "./MeshReport";
import { fillHoles, scaleFactorFor, type ScaleMode } from "@/lib/meshRepair";
import { parseNumber } from "../converters/numberInput";

type Target = "stl-bin" | "stl-ascii" | "obj" | "ply-bin" | "ply-ascii" | "glb" | "3mf";
const TARGETS: { id: Target; label: string; ext: string }[] = [
  { id: "stl-bin", label: "STL (binary, smaller)", ext: "stl" },
  { id: "stl-ascii", label: "STL (ASCII text)", ext: "stl" },
  { id: "obj", label: "OBJ", ext: "obj" },
  { id: "ply-bin", label: "PLY (binary, smaller)", ext: "ply" },
  { id: "ply-ascii", label: "PLY (ASCII text)", ext: "ply" },
  { id: "glb", label: "GLB (glTF binary)", ext: "glb" },
  { id: "3mf", label: "3MF (3D printing, millimetres)", ext: "3mf" },
];

const TOOL_INFO_CONTENT = {
  what: `Converts 3D mesh files between STL, OBJ, PLY (ASCII and binary), GLB (glTF) and 3MF, entirely in your browser. It merges duplicate vertices (so OBJ/PLY/GLB files are much smaller), writes face normals for STL output (OBJ, PLY and GLB are written without them), and checks the model (size, watertight, volume) before you download. You can tick \"Fill small holes\" to close open loops in a model that is not watertight, and scale the exported model by a factor or to a target size. A slider can also reduce the triangle count (mesh simplification) for big scans.`,
  how: `Upload a 3D file (the format is detected from its extension), choose what to convert to, and download. For GLB and 3MF, tell it the model's units, because glTF is always in metres and 3MF in millimetres.`,
  tip: `This converts geometry only: positions and triangles, with no colours, textures or materials. STL has no units; glTF/GLB uses metres, so pick the unit your model was made in (3D-printing models are usually millimetres). STEP and IGES are CAD formats that need specialised software.`,
};

export default function Mesh3DConverter() {
  const [file, setFile] = useState<File | null>(null);
  const [keepPct, setKeepPct] = useState(100);
  const [source, setSource] = useState<MeshFormat>("stl");
  const [target, setTarget] = useState<Target>("obj");
  const [units, setUnits] = useState("mm");
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<{ blob: Blob; name: string; mesh: Mesh; note: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<Mesh | null>(null);
  const [unitsGuessed, setUnitsGuessed] = useState(false);
  const [fill, setFill] = useState(false);
  const [scaleMode, setScaleMode] = useState<ScaleMode>("none");
  const [scaleText, setScaleText] = useState("");

  const reveal = useRevealDownload(!!result);

  // What the loaded file looks like before anything is changed: open edges decide whether "fill holes" is offered.
  const loadedInfo = useMemo(() => {
    if (!loaded) return null;
    const w = weldVertices(loaded);
    return { welded: w, checks: meshChecks(w), size: meshStats(w).size };
  }, [loaded]);
  const factor = loadedInfo ? scaleFactorFor(scaleMode, parseNumber(scaleText), loadedInfo.welded) : 1;
  const scaleInvalid = scaleMode !== "none" && factor === null;

  const handleFiles = useCallback(async (files: File[]) => {
    const f = files[0];
    if (!f) return;
    const detected = detectMeshFormat(f.name);
    if (!detected) { toast.error("Unsupported file type. Use STL, OBJ, PLY, GLB or 3MF."); return; }
    setFile(f); setResult(null); setFill(false); setScaleMode("none"); setScaleText(""); setSource(detected); setError(null); setLoaded(null); setUnitsGuessed(false);
    setUnits(detected === "glb" ? "m" : "mm"); // STL from CAD and printing is usually mm; OBJ/PLY are guessed below
    if (detected === "obj") setTarget("stl-bin"); else if (detected === "stl") setTarget("obj");
    // Parse now so a bad file is reported inline straight away. Nothing is charged here: a trial use is
    // only counted when the converted file is downloaded.
    setProcessing(true);
    try {
      const raw = await loadMeshFile(f, detected);
      const problem = meshProblem(raw);
      if (problem) { setError(problem); return; }
      setLoaded(raw);
      if (detected === "obj" || detected === "ply") { setUnits(guessUnits(raw)); setUnitsGuessed(true); }
    } catch (e) {
      setError(e instanceof Error && e.message ? `Couldn't read this file: ${e.message}` : "Couldn't read this file. Check it is a valid 3D file.");
    } finally { setProcessing(false); }
  }, []);

  const convert = async () => {
    if (!file || !loaded) return;
    setProcessing(true);
    try {
      const raw = loaded;
      const welded00 = weldVertices(raw);
      // Only when the person ticked it: close small open loops, then everything below (checks, size) sees the repaired mesh.
      const repair = fill ? fillHoles(welded00) : null;
      const welded0 = repair ? { ...repair.mesh, removedDegenerate: welded00.removedDegenerate } : welded00;
      const simplified = keepPct < 100 ? { ...simplifyMesh(welded0, welded0.triangles.length * keepPct / 100), removedDegenerate: welded0.removedDegenerate } : welded0;
      if (factor === null) throw new Error("Enter a scale above zero");
      const welded = factor === 1 ? simplified : { ...scaleMesh(simplified, factor), removedDegenerate: simplified.removedDegenerate };
      const t = TARGETS.find((x) => x.id === target)!;
      const base = file.name.replace(/\.[^.]+$/, "");
      let blob: Blob;
      let note = "";
      if (target === "stl-bin") blob = new Blob([writeSTLBinary(welded)], { type: "application/sla" });
      else if (target === "stl-ascii") blob = new Blob([writeSTLAscii(welded, base)], { type: "application/sla" });
      else if (target === "obj") blob = new Blob([writeOBJ(welded)], { type: "text/plain" });
      else if (target === "ply-bin") blob = new Blob([writePLYBinary(welded)], { type: "application/octet-stream" });
      else if (target === "ply-ascii") blob = new Blob([writePLYAscii(welded)], { type: "text/plain" });
      else if (target === "3mf") {
        const f = UNIT_FACTORS_TO_METRES[units].f / 0.001; // to millimetres
        blob = await write3MF(scaleMesh(welded, f), base);
        if (f !== 1) note = `Scaled from ${UNIT_FACTORS_TO_METRES[units].label.toLowerCase()} to millimetres for 3MF.`;
      } else {
        const f = UNIT_FACTORS_TO_METRES[units].f;
        blob = new Blob([writeGLB(scaleMesh(welded, f))], { type: "model/gltf-binary" });
        if (f !== 1) note = `Scaled from ${UNIT_FACTORS_TO_METRES[units].label.toLowerCase()} to metres for glTF.`;
      }
      if (repair) note += repair.filled > 0 ? ` Filled ${repair.filled} hole${repair.filled === 1 ? "" : "s"} (${repair.addedTriangles.toLocaleString()} triangles added).` : "";
      if (repair && repair.skipped > 0) note += ` ${repair.skipped} open loop${repair.skipped === 1 ? " was" : "s were"} left as is (too large, or touching another hole).`;
      if (factor !== 1) note += ` Scaled by ${Number(factor.toPrecision(6)).toLocaleString(undefined, { maximumFractionDigits: 6 })}x.`;
      if (keepPct < 100) note += ` Reduced from ${welded0.triangles.length.toLocaleString()} to ${simplified.triangles.length.toLocaleString()} triangles.`;
      if (welded.removedDegenerate > 0) note += ` ${welded.removedDegenerate.toLocaleString()} zero-area triangle(s) were dropped.`;
      setResult({ blob, name: `${base}.${t.ext}`, mesh: welded, note: note.trim() });
      toast.success(`Converted ${welded.triangles.length.toLocaleString()} triangles`);
    } catch (e) {
      setError(e instanceof Error && e.message ? `Couldn't convert: ${e.message}` : "Couldn't convert this file. Check it is a valid 3D file.");
    } finally { setProcessing(false); }
  };

  return (
    <ToolContainer>
      {!file ? (
        <FileDropZone gateAtDownload accept={MESH_ACCEPT} onFiles={handleFiles} label="Drop an STL, OBJ, PLY, GLB or 3MF file" description="Convert between 3D mesh formats — runs entirely in your browser" />
      ) : (
        <div className="space-y-4">
          <div className="p-4 rounded-lg border bg-card flex items-center justify-between gap-2">
            <p className="text-sm font-medium truncate">{file.name} <span className="text-xs text-muted-foreground">({source.toUpperCase()})</span></p>
            <Button size="sm" variant="ghost" onClick={() => { setFile(null); setResult(null); setError(null); setLoaded(null); setFill(false); setScaleMode("none"); setScaleText(""); }}>Change file</Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="mb-2 block">Convert to</Label>
              <Select value={target} onValueChange={(v) => { setTarget(v as Target); setResult(null); }}>
                <SelectTrigger aria-label="Convert to"><SelectValue /></SelectTrigger>
                <SelectContent>{TARGETS.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-2 block">Model units</Label>
              <Select value={units} onValueChange={(v) => { setUnits(v); setUnitsGuessed(false); setResult(null); }}>
                <SelectTrigger aria-label="Model units"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(UNIT_FACTORS_TO_METRES).map(([k, u]) => <SelectItem key={k} value={k}>{u.label}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">{unitsGuessed ? "Guessed from the model's size (OBJ and PLY files don't store units). Change it if the size below looks wrong. " : ""}{target === "glb" ? "glTF is always in metres, so the model is scaled." : target === "3mf" ? "3MF is always in millimetres, so the model is scaled if needed." : "Used for the size and fits-on-bed checks."}</p>
            </div>
          </div>
          <div>
            <Label htmlFor="keep-pct" className="mb-2 block">Reduce triangles: keep {keepPct}%{keepPct < 100 ? " (smaller file, less detail)" : " (no reduction)"}</Label>
            <input id="keep-pct" type="range" min={5} max={100} step={5} value={keepPct} onChange={(e) => { setKeepPct(Number(e.target.value)); setResult(null); }} className="w-full max-w-sm" />
            <p className="text-xs text-muted-foreground mt-1">Merges the least important edges first, keeping the shape, the outline and closed surfaces. Try 50% for a big scan; large models can take several seconds.</p>
          </div>
          {loadedInfo && (
            <div className="space-y-3 rounded-lg border p-3" data-testid="mesh-options">
              <div>
                <p className="text-sm font-medium mb-1">Repair</p>
                {loadedInfo.checks.watertight ? (
                  <p className="text-xs text-muted-foreground">This model is already watertight, so there is nothing to fill.</p>
                ) : (
                  <>
                    <p className="text-xs font-medium mb-2">⚠ Not watertight: {loadedInfo.checks.openEdges.toLocaleString()} open edge{loadedInfo.checks.openEdges === 1 ? "" : "s"}{loadedInfo.checks.nonManifoldEdges ? ` and ${loadedInfo.checks.nonManifoldEdges.toLocaleString()} edge(s) shared by 3+ faces` : ""}.</p>
                    <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1" checked={fill} onChange={(e) => { setFill(e.target.checked); setResult(null); }} /><span>Fill small holes (open loops of up to 100 edges) when converting. Your file is only changed if this is ticked, and the result is re-checked below.</span></label>
                    {loadedInfo.checks.nonManifoldEdges > 0 && <p className="text-xs text-muted-foreground mt-1">Edges shared by 3+ faces can't be fixed by filling holes.</p>}
                  </>
                )}
              </div>
              <div>
                <Label className="mb-1 block text-sm font-medium">Scale</Label>
                <div className="flex flex-wrap items-center gap-2">
                  <Select value={scaleMode} onValueChange={(v) => { setScaleMode(v as ScaleMode); setResult(null); }}>
                    <SelectTrigger aria-label="Scale mode" className="w-56"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Keep the size</SelectItem>
                      <SelectItem value="factor">Multiply by a factor</SelectItem>
                      <SelectItem value="largest">Set the largest side to</SelectItem>
                    </SelectContent>
                  </Select>
                  {scaleMode !== "none" && <Input aria-label={scaleMode === "factor" ? "Scale factor" : `Largest side (${units})`} className="h-9 w-28" inputMode="decimal" placeholder={scaleMode === "factor" ? "10" : "50"} value={scaleText} onChange={(e) => { setScaleText(e.target.value); setResult(null); }} />}
                  {scaleMode === "largest" && <span className="text-xs text-muted-foreground">{units}</span>}
                  {scaleMode === "factor" && [0.1, 10, 25.4].map((f) => <Button key={f} type="button" size="sm" variant="outline" onClick={() => { setScaleText(String(f)); setResult(null); }}>×{f}</Button>)}
                </div>
                <p className={`text-xs mt-1 ${scaleInvalid ? "text-destructive" : "text-muted-foreground"}`}>
                  {scaleMode === "none" ? "Applies to the exported model, in the units chosen above." : scaleInvalid ? "Enter a number above zero." : `Exported size: ${loadedInfo.size.map((n) => Number((n * (factor ?? 1)).toPrecision(6)).toLocaleString(undefined, { maximumFractionDigits: 4 })).join(" × ")} ${units}`}
                </p>
              </div>
            </div>
          )}
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          {processing ? <ProcessingState /> : <Button onClick={convert} disabled={!loaded || !!error || scaleInvalid}>Convert</Button>}
          {result && (
            <Card className="p-4 space-y-3">
              <MeshReport mesh={result.mesh} unitLabel={units} />
              {result.note && <p className="text-xs text-muted-foreground">{result.note}</p>}
              <p className="text-xs text-muted-foreground">Output size: {(result.blob.size / 1024).toLocaleString(undefined, { maximumFractionDigits: 1 })} KB</p>
              <div ref={reveal.ref} className={reveal.className}><DownloadButton blob={result.blob} fileName={result.name} /></div>
            </Card>
          )}
        </div>
      )}
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
